import { ActionTypeCode, AdjustedProductType, DepositDetail, LiquidityPeriodBalanceType, LoanDetails, PolicyOrAccount, Product, ProductType, RepaymentFrequency, RepaymentType } from "../interfaces/DTO/mislaka_xml_models";
import { calculateRemainingMonths } from "../utils/dateCalculators";
import { MimshakService } from "./mimshakService";
import { MimshakData } from "../interfaces/DTO/mimshakData";
import _ from "lodash";

export class MimshakGroupByService {
   
  static groupByHandlerCode(products: Product[], balanceType?: LiquidityPeriodBalanceType): Record<string, number> {
    const groupedData = products.reduce((acc, product) => {
      const handlerCode = product.details.handler_code;
      
      // ✅ בחירה בין calculateTotalsByType ל-calculateTotals לפי balanceType
      const totalAmount = balanceType
        ? MimshakService.calculateTotalsByType(product.policies_or_accounts, balanceType) || 0
        : MimshakService.calculateTotals(product.policies_or_accounts) || 0;

        if (totalAmount > 0) {
          acc[handlerCode] = (acc[handlerCode] || 0) + totalAmount;
        }
      return acc;
    }, {} as Record<string, number>);

    return groupedData;
  }

static groupByProductType(products: Product[], balanceType?: LiquidityPeriodBalanceType): Record<ProductType, number> {
  const groupedData = products.reduce((acc, product) => {
    let productCode: ProductType = product.details.product_type;
    const adjusted = product.details.adjusted_product_type;

    // ✅ מיפוי מחדש אם מדובר בביטוח מנהלים במסווה של פוליסת חיסכון טהור
    if (
      productCode === ProductType.PURE_SAVINGS_POLICY &&
      (adjusted === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
        adjusted === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE)
    ) {
      productCode = ProductType.LIFE_INSURANCE_SAVINGS;
    }

    const totalAmount = balanceType
      ? MimshakService.calculateTotalsByType(product.policies_or_accounts, balanceType) || 0
      : MimshakService.calculateTotals(product.policies_or_accounts) || 0;

    if (totalAmount > 0) {
      acc[productCode] = (acc[productCode] || 0) + totalAmount;
    }

    return acc;
  }, {} as Record<ProductType, number>);

  return groupedData;
}

 static groupAllByProductTypeAndHandlerCode(
  products: Product[],
  balanceType?: LiquidityPeriodBalanceType
): Record<string | number, Record<string, number>> {
  const groupedData: Record<string | number, Record<string, number>> = {};

  products.forEach((product) => {
    let productType = product.details.product_type;
    const adjusted = product.details.adjusted_product_type;
    const handlerCode = product.details.handler_code;

    // ✅ מיפוי מחדש אם מדובר בביטוח מנהלים במסווה של פוליסת חיסכון טהור
    if (
      productType === ProductType.PURE_SAVINGS_POLICY &&
      (adjusted === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
        adjusted === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE)
    ) {
      productType = ProductType.LIFE_INSURANCE_SAVINGS;
    }

    const totalAmount = balanceType
      ? MimshakService.calculateTotalsByType(product.policies_or_accounts, balanceType) || 0
      : MimshakService.calculateTotals(product.policies_or_accounts) || 0;

    if (totalAmount > 0) {
      if (!groupedData[productType]) {
        groupedData[productType] = {};
      }

      groupedData[productType][handlerCode] =
        (groupedData[productType][handlerCode] || 0) + totalAmount;
    }
  });

  return groupedData;
}

  /**
   * Merge products by ProductType, ClientID, and HandlerCode
   * @param products - Array of Product objects
   * @returns Merged list of Product[]
   */
  static mergeProducts(products: Product[]): Product[] {
    const groupedData: Record<string, Product> = {};

    products.forEach((product) => {
      const productType = product.details.product_type;
      const handlerCode = product.details.handler_code;
      const clientId = product.client?.client_id; 

      // יצירת מפתח ייחודי לכל קבוצה
      const key = `${productType}_${clientId}_${handlerCode}`;

      if (!groupedData[key]) {
        // אם אין עדיין קבוצה עם אותו מפתח, ניצור אחת חדשה
        groupedData[key] = {
          details: product.details,
          employer: product.employer,
          client: product.client,
          policies_or_accounts: [...product.policies_or_accounts], // התחלת מערך הפוליסות
        };
      } else {
        // אם כבר קיימת קבוצה, נוסיף את הפוליסות למערך המאוחד
        groupedData[key].policies_or_accounts.push(...product.policies_or_accounts);
      }
    });

    // המרה חזרה למערך של מוצרים ממוזגים
    return Object.values(groupedData);
  }
  static flattenLiquiditySummaries(
    products: Product[],
    referenceDate: Date
  ): LiquiditySummaryDetail[] {
    const referenceYear = referenceDate.getFullYear();
    const referenceMonth = referenceDate.getMonth() + 1; // חודשים בפורמט 1-12

    const flatList: LiquiditySummaryDetail[] = [];

    products.forEach((product) => {
      product.policies_or_accounts.forEach((policy) => {
        const capitalAmount = (policy.accumulated_savings ?? [])
          .filter((s) => s.savings_type_code === 1) // CAPITAL = 1
          .reduce((sum, s) => sum + s.total_savings, 0);
        const nonCapitalAmount = (policy.accumulated_savings ?? [])
          .filter((s) => s.savings_type_code !== 1)
          .reduce((sum, s) => sum + s.total_savings, 0);
       

        if (policy.liquidity_summaries.length === 0) {
          // אם אין נתוני נזילות, יוצרים רשומה עם UNKNOWN
          flatList.push({
            product_type: product.details.product_type,
            handler_code: product.details.handler_code,
            policy_number: policy.policy_number,
            amount: 0,
            liquidityDate: null,
            liquidityDateCategory: "UNKNOWN",
          });
        } else {
          // יצירת רשומות מתוך ה-LiquiditySummary
          policy.liquidity_summaries.forEach((summary) => {
            const liquidityDate = summary.liquidity_date ? new Date(summary.liquidity_date) : null;
            let liquidityDateCategory = "UNKNOWN";

            if (liquidityDate) {
              const liquidityYear = liquidityDate.getFullYear();
              const liquidityMonth = liquidityDate.getMonth() + 1; // חודשים בפורמט 1-12

              if (liquidityYear < referenceYear) {
                liquidityDateCategory = "ALREADY_LIQUID";
              } else if (liquidityYear === referenceYear) {
                liquidityDateCategory =
                  liquidityMonth <= referenceMonth ? "ALREADY_LIQUID" : "LIQUID_THIS_YEAR";
              } else {
                liquidityDateCategory = liquidityYear.toString();
              }
            }

          

            flatList.push({
              product_type: product.details.product_type,
              handler_code: product.details.handler_code,
              policy_number: policy.policy_number,
              amount: summary.liquidity_amount,
              liquidityDate: summary.liquidity_date,
              liquidityDateCategory,
            });
          });
        }
      });
    });

    return flatList;
  }
  static groupByDepositsFromStartOfYear(
    policies: PolicyOrAccount[],
    isoDate: string,
    dateMode: 'salary_month' | 'payment_period' | 'deposit_date' = 'payment_period'
  ): Record<number, number> {
  const referenceDate = new Date(isoDate);
  const currentYear = referenceDate.getFullYear();

  const allPeriods: number[] = [];
  for (let m = 0; m <= referenceDate.getMonth(); m++) {
    const month = (m + 1).toString().padStart(2, "0");
    allPeriods.push(Number(`${currentYear}${month}`));
  }

  const depositsFromStartOfYear = this.getDepositsFromStartOfYear(policies, isoDate, dateMode);

  const aggregatedDeposits: Record<number, number> = {};
  allPeriods.forEach(period => {
    aggregatedDeposits[period] = 0;
  });

  depositsFromStartOfYear.forEach(deposit => {
    const { payment_period, salary_month, deposit_date, deposit_amount_paid } = deposit;

    // Select period based on dateMode
    let period: number;
    if (dateMode === 'deposit_date' && deposit_date) {
      // Extract YYYYMM from deposit_date (format: YYYY-MM-DD)
      const [year, month] = deposit_date.split('-');
      period = parseInt(`${year}${month}`);
    } else if (dateMode === 'salary_month' && salary_month && salary_month.trim() !== '') {
      period = parseInt(salary_month);
    } else {
      period = payment_period;
    }

    if (!aggregatedDeposits[period]) {
      aggregatedDeposits[period] = 0;
    }

    aggregatedDeposits[period] += deposit_amount_paid;
  });

  return aggregatedDeposits;
}
  static getDepositsFromStartOfYear(
    policies: PolicyOrAccount[],
    isoDate: string,
    dateMode: 'salary_month' | 'payment_period' | 'deposit_date' = 'payment_period'
  ): DepositDetail[] {
  const referenceDate = new Date(isoDate);
  if (isNaN(referenceDate.getTime())) {
    throw new Error("Invalid ISO date provided");
  }

  const currentYear = referenceDate.getFullYear();
  const currentMonth = referenceDate.getMonth() + 1; // 1-12

  const allDeposits: DepositDetail[] = policies.flatMap((policy) => policy.deposits);

  return allDeposits.filter((deposit) => {
    if (dateMode === 'deposit_date') {
      // Filter by deposit_date: YYYY-MM-DD format
      if (!deposit.deposit_date) return false;
      const depositDate = new Date(deposit.deposit_date);
      const year = depositDate.getFullYear();
      const month = depositDate.getMonth() + 1; // 1-12

      // Include deposits from January of current year up to current month
      return year === currentYear && month >= 1 && month <= currentMonth;
    } else if (dateMode === 'salary_month' && deposit.salary_month && deposit.salary_month.trim() !== '') {
      // Filter by salary_month: YYYYMM format
      const salaryMonthValue = parseInt(deposit.salary_month);
      const year = Math.floor(salaryMonthValue / 100);
      const month = salaryMonthValue % 100;

      // Include deposits from January of current year up to current month
      return year === currentYear && month >= 1 && month <= currentMonth;
    } else {
      // Filter by payment_period: YYYYMM format
      const periodValue = deposit.payment_period;
      const year = Math.floor(periodValue / 100);
      const month = periodValue % 100;

      // Include deposits from January of current year up to current month
      return year === currentYear && month >= 1 && month <= currentMonth;
    }
  });
}
  static groupByDepositsByPeriodFillingEmptyLast12(
    policies: PolicyOrAccount[],
    isoDate: string,
    dateMode: 'salary_month' | 'payment_period' | 'deposit_date' = 'payment_period'
  ): Record<number, number> {
    // שליפת רשימת 12 החודשים האחרונים באמצעות הפונקציה החדשה
    const allPeriods = this.getLast12MonthsPeriods(isoDate);

    // **שליפת כל ההפקדות מ-12 החודשים האחרונים**
    const recentDepositsByDepositDate: DepositDetail[] = MimshakService.getDepositsForLast12Months(policies, isoDate, dateMode);

    // **יצירת מבנה האגרגציה עם כל 12 החודשים**
    const aggregatedDeposits: Record<number, number> = {};
    allPeriods.forEach(period => {
      aggregatedDeposits[period] = 0;
    });

    // **סכימת הפקדות לפי dateMode**
    recentDepositsByDepositDate.forEach(deposit => {
      const { payment_period, salary_month, deposit_date, deposit_amount_paid } = deposit;

      // Select period based on dateMode
      let period: number;
      if (dateMode === 'deposit_date' && deposit_date) {
        // Extract YYYYMM from deposit_date (format: YYYY-MM-DD)
        const [year, month] = deposit_date.split('-');
        period = parseInt(`${year}${month}`);
      } else if (dateMode === 'salary_month' && salary_month && salary_month.trim() !== '') {
        period = parseInt(salary_month);
      } else {
        period = payment_period;
      }

      if (!aggregatedDeposits[period]) {
        aggregatedDeposits[period] = 0;
      }

      aggregatedDeposits[period] += deposit_amount_paid;
    });

    return aggregatedDeposits;
  }
  static getMonthsUntilEndOfYear = (referenceDate: string): number[] => {
    const result: number[] = [];
    const date = new Date(referenceDate);
    date.setDate(1);
  
    while (date.getFullYear() === new Date().getFullYear()) {
      const y = date.getFullYear();
      const m = (date.getMonth() + 1).toString().padStart(2, "0");
      result.push(Number(`${y}${m}`));
      date.setMonth(date.getMonth() + 1);
    }
  
    return result;
  };
   static getLast12MonthsPeriods(isoDate: string): number[] {
    // המרה של `isoDate` לאובייקט `Date`
    const referenceDate = new Date(isoDate);
    if (isNaN(referenceDate.getTime())) {
      throw new Error("Invalid ISO date provided");
    }

    // יצירת רשימת 12 החודשים האחרונים
    const allPeriods: number[] = [];
    for (let i = 0; i < 12; i++) {
      const year = referenceDate.getFullYear();
      const month = (referenceDate.getMonth() + 1).toString().padStart(2, "0");
      allPeriods.push(Number(`${year}${month}`));

      // עדכון התאריך חודש אחורה
      referenceDate.setMonth(referenceDate.getMonth() - 1);
    }

    return allPeriods;
  }

static groupByDepositsByPeriodAndCategoryTypeFillingEmptyLast12(
  policies: PolicyOrAccount[],
  isoDate: string,
  groupByField: "payment_period" | "salary_month" = "payment_period"
): Record<string | number, Record<number, number>> {
  const allPeriods = this.getLast12MonthsPeriods(isoDate);
  const recentDeposits: DepositDetail[] = MimshakService.getDepositsForLast12Months(policies, isoDate);

  const aggregatedDeposits: Record<string | number, Record<number, number>> = {};

  // הגדרת כל התקופות מראש עם אובייקטים ריקים
  allPeriods.forEach(period => {
    aggregatedDeposits[period] = {};
  });

  // סכימה לפי השדה שנבחר
  recentDeposits.forEach(deposit => {
    const periodKey = deposit[groupByField];
    const { contribution_type, deposit_amount_paid } = deposit;

    if (!aggregatedDeposits[periodKey]) {
      aggregatedDeposits[periodKey] = {};
    }

    if (!aggregatedDeposits[periodKey][contribution_type]) {
      aggregatedDeposits[periodKey][contribution_type] = 0;
    }

    aggregatedDeposits[periodKey][contribution_type] += deposit_amount_paid;
  });

  // השלמת חוסרים
  const allContributionTypes = new Set(recentDeposits.map(d => d.contribution_type));

  Object.keys(aggregatedDeposits).forEach(period => {
    allContributionTypes.forEach(contributionType => {
      if (!aggregatedDeposits[period][contributionType]) {
        aggregatedDeposits[period][contributionType] = 0;
      }
    });
  });

  return aggregatedDeposits;
}

  static groupByDepositsByPeriodAndProductTypeFillingEmptyLast12(
    products: Product[],
    isoDate: string
  ): Record<number, Record<string, number>> {
    const allPeriods = this.getLast12MonthsPeriods(isoDate);
  
    // שליפת כל ההפקדות עם product_type מה-12 חודשים האחרונים
    const depositsWithType: {
      payment_period: number;
      deposit_amount_paid: number;
      product_type: number;
    }[] = [];
  
    products.forEach((product) => {
      const productType = product.details.product_type;
  
      const deposits = MimshakService.getDepositsForLast12Months(
        product.policies_or_accounts,
        isoDate
      );
  
      deposits.forEach((deposit) => {
        depositsWithType.push({
          payment_period: deposit.payment_period,
          deposit_amount_paid: deposit.deposit_amount_paid,
          product_type: productType,
        });
      });
    });
  
    // בניית מבנה בסיסי עם כל החודשים
    const aggregated: Record<number, Record<string, number>> = {};
    allPeriods.forEach((period) => {
      aggregated[period] = {};
    });
  
    // סכימה לפי period ו-product_type
    depositsWithType.forEach(({ payment_period, product_type, deposit_amount_paid }) => {
      const typeKey = product_type.toString();
  
      if (!aggregated[payment_period]) {
        aggregated[payment_period] = {};
      }
  
      if (!aggregated[payment_period][typeKey]) {
        aggregated[payment_period][typeKey] = 0;
      }
  
      aggregated[payment_period][typeKey] += deposit_amount_paid;
    });
  
    // השלמת חוסרים: כל product_type יופיע בכל תקופה
    const allTypes = new Set(depositsWithType.map((d) => d.product_type.toString()));
  
    allPeriods.forEach((period) => {
      allTypes.forEach((typeKey) => {
        if (!aggregated[period][typeKey]) {
          aggregated[period][typeKey] = 0;
        }
      });
    });
  
    return aggregated;
  }
  static groupWithdrawalsByPeriodAndProductTypeFillingEmptyLast12(
    products: Product[],
    isoDate: string
  ): Record<number, Record<string, number>> {
    const allPeriods = this.getLast12MonthsPeriods(isoDate);
  
    const withdrawalsWithType: {
      period: number;
      withdrawal_amount: number;
      product_type: number;
    }[] = [];
  
    products.forEach((product) => {
      const productType = product.details.product_type;
  
      product.policies_or_accounts.forEach((policy) => {
        (policy.fund_actions || []).forEach((action) => {
          if (action.action_type_code === ActionTypeCode.WITHDRAWAL && action.execution_date) {
            const [year, month] = action.execution_date.split("-");
            const period = Number(`${year}${month}`);
            withdrawalsWithType.push({
              period,
              withdrawal_amount: action.withdrawal_amount,
              product_type: productType,
            });
          }
        });
      });
    });
  
    const aggregated: Record<number, Record<string, number>> = {};
    allPeriods.forEach((period) => {
      aggregated[period] = {};
    });
  
    withdrawalsWithType.forEach(({ period, product_type, withdrawal_amount }) => {
      const typeKey = product_type.toString();
  
      if (!aggregated[period]) {
        aggregated[period] = {};
      }
  
      if (!aggregated[period][typeKey]) {
        aggregated[period][typeKey] = 0;
      }
  
      aggregated[period][typeKey] += withdrawal_amount;
    });
  
    const allTypes = new Set(withdrawalsWithType.map((w) => w.product_type.toString()));
  
    allPeriods.forEach((period) => {
      allTypes.forEach((typeKey) => {
        if (!aggregated[period][typeKey]) {
          aggregated[period][typeKey] = 0;
        }
      });
    });
  
    return aggregated;
  }
static getMinNonZeroDepositsMergedByProductType(
  products: Product[],
  isoDate: string,
  mimshakData?: MimshakData[]
): Record<number, number> {
  const referenceDate = new Date(isoDate);
  const threeMonthsAgo = new Date(referenceDate);
  threeMonthsAgo.setMonth(referenceDate.getMonth() - 3);

  const perProductType: Record<number, number[]> = {};

  products.forEach((product) => {
    let productType = product.details.product_type;
    const adjusted = product.details.adjusted_product_type;

    // מיפוי מחדש אם מדובר בביטוח מנהלים במסווה של פוליסת חיסכון טהור
    if (
      productType === ProductType.PURE_SAVINGS_POLICY &&
      (adjusted === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
        adjusted === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE)
    ) {
      productType = ProductType.LIFE_INSURANCE_SAVINGS;
    }

    // סינון מוצרים ללא הון - לא לוקחים הפקדות ממוצר שאין בו הון
    const capitalAmount = MimshakService.calculateTotalsByType(product.policies_or_accounts, LiquidityPeriodBalanceType.CAPITAL);
    if (!capitalAmount || capitalAmount <= 0) {
      return;
    }

    // ביטוח מנהלים וקופת גמל - תמיד הפקדה 0 (אין הפקדה שוטפת רלוונטית)
    if (productType === ProductType.LIFE_INSURANCE_SAVINGS || productType === ProductType.PROVIDENT_FUND) {
      if (!perProductType[productType]) {
        perProductType[productType] = [];
      }
      perProductType[productType].push(0);
      return;
    }

    // עבור קופת גמל להשקעה - צבירת recurrent_deposit מ-MimshakData
    if (productType === ProductType.INVESTMENT_PROVIDENT_FUND && mimshakData) {
      // מציאת כל ה-MimshakData הרלוונטיים לפוליסות של המוצר הזה
      const policyNumbers = product.policies_or_accounts.map(p => p.policy_number);
      const relevantMimshakData = mimshakData.filter(
        md => md.product_type === ProductType.INVESTMENT_PROVIDENT_FUND &&
              policyNumbers.includes(md.policy_number)
      );

      // צבירת כל ה-recurrent_deposit עבור הפוליסות
      const totalRecurrentDeposit = relevantMimshakData.reduce(
        (sum, md) => sum + (md.recurrent_deposit || 0),
        0
      );

      if (!perProductType[productType]) {
        perProductType[productType] = [];
      }
      perProductType[productType].push(totalRecurrentDeposit);
      return; // סיימנו עם המוצר הזה
    }

    const deposits = MimshakService.getDepositsForLast12Months(product.policies_or_accounts, isoDate)
      .filter(d => {
        const date = new Date(d.deposit_date);
        return date >= threeMonthsAgo && date <= referenceDate;
      });

    const monthlyTotals = new Map<string, number>();

    deposits.forEach((deposit) => {
      const shouldUse =
        (productType === ProductType.EDUCATION_FUND && deposit.salary_month) ||
        (productType !== ProductType.EDUCATION_FUND) ||
        (!deposit.salary_month && deposit.payment_period); // fallback

      if (shouldUse && deposit.deposit_amount_paid > 0) {
        const key = deposit.salary_month || deposit.payment_period?.toString() || deposit.deposit_date.slice(0, 7);
        if (!monthlyTotals.has(key)) {
          monthlyTotals.set(key, 0);
        }
        monthlyTotals.set(key, monthlyTotals.get(key)! + deposit.deposit_amount_paid);
      }
    });

    const monthlySums = Array.from(monthlyTotals.values());
    const minAmount = monthlySums.filter(x => x > 0).sort((a, b) => a - b)[0] || 0;

    if (!perProductType[productType]) {
      perProductType[productType] = [];
    }
    perProductType[productType].push(minAmount);
  });

  const result: Record<number, number> = {};
  Object.entries(perProductType).forEach(([typeStr, mins]) => {
    const pt = parseInt(typeStr);
    result[pt] = mins.reduce((sum, val) => sum + val, 0);
  });

  return result;
}


  static groupLiquiditySummaryByYear(products: Product[]): GroupedLiquiditySummary[] {

      // מציאת `update_date` מהפוליסה הראשונה או השנייה
      let referenceDate: Date = new Date();
      
      for (const product of products) {
        if (product.policies_or_accounts.length > 0) {
          const firstPolicy = product.policies_or_accounts[0];
          if (firstPolicy.update_date) {
            const parsedDate = new Date(firstPolicy.update_date);
            if (!isNaN(parsedDate.getTime())) {
              referenceDate = parsedDate;
              break;
            }
          }
          if (product.policies_or_accounts.length > 1) {
            const secondPolicy = product.policies_or_accounts[1];
            if (secondPolicy.update_date) {
              const parsedDate = new Date(secondPolicy.update_date);
              if (!isNaN(parsedDate.getTime())) {
                referenceDate = parsedDate;
                break;
              }
            }
          }
        }
      }
  
      // שליפת הרשימה השטוחה עם referenceDate
      const flatList: LiquiditySummaryDetail[] = MimshakGroupByService.flattenLiquiditySummaries(products, referenceDate);
  
      // הגדרת המפה עם `Type Safety`
      const groupedData: GroupedLiquiditySummaryMap = {};
  
      flatList.forEach((item) => {
        if (!groupedData[item.liquidityDateCategory]) {
          groupedData[item.liquidityDateCategory] = { totalAmount: 0, objectList: [] };
        }
        groupedData[item.liquidityDateCategory].totalAmount += item.amount;
        groupedData[item.liquidityDateCategory].objectList.push(item);
      });
  
      // המרה לרשימה מסודרת
      return Object.entries(groupedData)
        .map(([liquidityDateCategory, { totalAmount, objectList }]) => ({
          liquidityDateCategory,
          totalAmount,
          objectList,
        }))
        .sort((a, b) => {
          if (a.liquidityDateCategory === "ALREADY_LIQUID") return -1;
          if (b.liquidityDateCategory === "ALREADY_LIQUID") return 1;
        
          if (a.liquidityDateCategory === "LIQUID_THIS_YEAR") return -1;
          if (b.liquidityDateCategory === "LIQUID_THIS_YEAR") return 1;
        
          if (a.liquidityDateCategory === "UNKNOWN") return 1;
          if (b.liquidityDateCategory === "UNKNOWN") return -1;
        
          return parseInt(a.liquidityDateCategory) - parseInt(b.liquidityDateCategory);
        });
    }

  static mergeRetirementBuckets(
    summaries: GroupedLiquiditySummary[],
    dateOfBirths: (string | null | undefined)[],
    retirementAge: number = 67
  ): GroupedLiquiditySummary[] {
    const result: GroupedLiquiditySummary[] = [];
    let retirementBucket: GroupedLiquiditySummary | null = null;

    for (const s of summaries) {
      const year = parseInt(s.liquidityDateCategory);
      if (!isNaN(year)) {
        const ages = dateOfBirths
          .filter(Boolean)
          .map((dob) => {
            const birth = new Date(dob!);
            return year - birth.getFullYear();
          });
        const minAge = ages.length > 0 ? Math.min(...ages) : null;
        if (minAge === null || minAge >= retirementAge) {
          if (retirementBucket) {
            retirementBucket.totalAmount += s.totalAmount;
            retirementBucket.objectList.push(...s.objectList);
          } else {
            retirementBucket = { liquidityDateCategory: "RETIREMENT_AND_ABOVE", totalAmount: s.totalAmount, objectList: [...s.objectList] };
          }
          continue;
        }
      }
      result.push(s);
    }

    if (retirementBucket) result.push(retirementBucket);
    return result;
  }

    static getPensionGroupedSummary = (products: Product[]): GroupedLiquiditySummary => {
      const seenPolicies = new Set<string>(); // מזהה ייחודי למניעת כפילויות
    
      const pensionPolicies = products.flatMap((product) =>
        product.policies_or_accounts
          .filter((policy) => {
            // מזהה ייחודי לכל פוליסה
            const uniqueId = `${product.details.product_type}-${policy.policy_number}`;
            const hasPension = policy.pension_fund_details?.some((fund) => fund.expected_monthly_pension > 0);
    
            if (hasPension && !seenPolicies.has(uniqueId)) {
              seenPolicies.add(uniqueId);
              return true;
            }
            return false;
          })
          .map((policy) => ({ product, policy })) // שמירת הקשר בין `product` ל-`policy`
      );
    
      // חישוב סכום הקצבה הכולל
      const totalMonthlyPension = pensionPolicies.reduce(
        (sum, { policy }) => sum + MimshakService.calculateTotalExpectedMonthlyPension(policy),
        0
      );
    
      // יצירת אובייקטי LiquiditySummaryDetail לפוליסות המתאימות
      const pensionDetails: LiquiditySummaryDetail[] = pensionPolicies.map(({ product, policy }) => ({
        product_type: product.details.product_type,
        handler_code: product.details.handler_code,
        policy_number: policy.policy_number,
        totalAmount: MimshakService.calculateTotalExpectedMonthlyPension(policy),
        amount: MimshakService.calculateTotalExpectedMonthlyPension(policy),
        liquidityDate: "",
        liquidityDateCategory: "PENSION_AT_67",
      }));
    
      // החזרת אובייקט עם התוצאה
      return {
        liquidityDateCategory: "PENSION_AT_67",
        totalAmount: totalMonthlyPension,
        objectList: pensionDetails.length > 0 ? pensionDetails : [],
      }
    }

    static getLoanRepaymentSchedule = (
      loanDetails: LoanDetails[],
      updateDate: string
    ): Record<number, number> => {
      const schedule: Record<number, number> = {};
    
      loanDetails.forEach((loan) => {
        if (!loan.loan_balance || !loan.loan_start_date || !loan.loan_end_date) return;
    
        const startDate = new Date(loan.loan_start_date);
        const update = new Date(updateDate);
        const endDate = new Date(loan.loan_end_date);
    
        const effectiveStart = new Date(Math.max(startDate.getTime(), update.getTime()));
        const months = calculateRemainingMonths(effectiveStart.toISOString().slice(0, 10), loan.loan_end_date);
        if (months <= 0) return;
    
        const frequency = loan.repayment_frequency ?? RepaymentFrequency.MONTHLY;
        const frequencyGap = {
          [RepaymentFrequency.MONTHLY]: 1,
          [RepaymentFrequency.QUARTERLY]: 3,
          [RepaymentFrequency.SEMI_ANNUAL]: 6,
          [RepaymentFrequency.ANNUAL]: 12,
          [RepaymentFrequency.OTHER]: 1,
        }[frequency];
    
        const numPayments = Math.ceil(months / frequencyGap);
        const periodicPayment = loan.periodic_repayment_amount || 0;
    
        for (let i = 0; i < numPayments; i++) {
          const d = new Date(effectiveStart);
          d.setDate(1); // יום ראשון של החודש כדי להימנע מקפיצות תאריך
          d.setMonth(d.getMonth() + i * frequencyGap);
          const y = d.getFullYear();
          const m = (d.getMonth() + 1).toString().padStart(2, "0");
          const period = Number(`${y}${m}`);
          schedule[period] = (schedule[period] || 0) + periodicPayment;
        }
      });
    
      return schedule;
    };

  /**
   * Group ManualMimshakData by product_type, summing funds_sum
   * @param manualMimshakData - Array of ManualMimshakData objects
   * @returns Record mapping product_type to total funds_sum
   */
  static groupManualMimshakByProductType(manualMimshakData: MimshakData[]): Record<number, number> {
    const groupedData = manualMimshakData.reduce((acc, item) => {
      const productType = item.product_type;
      const fundsSum = item.funds_sum || 0;

      if (fundsSum > 0) {
        acc[productType] = (acc[productType] || 0) + fundsSum;
      }

      return acc;
    }, {} as Record<number, number>);

    return groupedData;
  }

  /**
   * Group MimshakData by adjusted_product_type (falling back to product_type when null/undefined,
   * same fallback used across the app — see MimshakDataUtil / PolicyCard), summing funds_sum.
   * @param mimshakData - Array of MimshakData objects
   * @returns Record mapping adjusted_product_type (or product_type fallback) to total funds_sum
   */
  static groupByAdjustedProductType(mimshakData: MimshakData[]): Record<number, number> {
    const groupedData = mimshakData.reduce((acc, item) => {
      const productType = item.adjusted_product_type ?? item.product_type;
      const fundsSum = item.funds_sum || 0;

      if (fundsSum > 0) {
        acc[productType] = (acc[productType] || 0) + fundsSum;
      }

      return acc;
    }, {} as Record<number, number>);

    return groupedData;
  }

  /**
   * Group MimshakData by product_type, summing funds_sum.
   * Only for PURE_SAVINGS_POLICY the adjusted_product_type is used (when present).
   */
  static getProductTypeAdjustingPureSavings(item: { product_type: number; adjusted_product_type?: number | null }): number {
    return item.product_type === ProductType.PURE_SAVINGS_POLICY
      ? item.adjusted_product_type ?? item.product_type
      : item.product_type;
  }

  static groupByProductTypeAdjustingPureSavings(
    mimshakData: MimshakData[],
    getValue: (item: MimshakData) => number = (item) => item.funds_sum || 0
  ): Record<number, number> {
    return mimshakData.reduce((acc, item) => {
      const productType = MimshakGroupByService.getProductTypeAdjustingPureSavings(item);
      const value = getValue(item);

      if (value > 0) {
        acc[productType] = (acc[productType] || 0) + value;
      }

      return acc;
    }, {} as Record<number, number>);
  }

  /**
   * Color for a single deposit-timeline month cell — shared by DepositsTimeLine and any
   * component displaying the same green / gray / white / dark-red deposit-gap semantics.
   */
  static getDepositMonthColor(params: {
    monthKey: number;
    totalDeposit: number;
    minJoinDateKey: number | null;
    isNonRecurringDeposit: boolean;
  }): { color: string; textColor: string; beforeProductExisted: boolean } {
    const { monthKey, totalDeposit, minJoinDateKey, isNonRecurringDeposit } = params;
    const beforeProductExisted = totalDeposit === 0 && (minJoinDateKey === null || monthKey < minJoinDateKey);

    return {
      color: totalDeposit > 0 ? "green" : beforeProductExisted ? "#9e9e9e" : isNonRecurringDeposit ? "white" : "#8B0000",
      textColor: totalDeposit > 0 ? "white" : beforeProductExisted ? "white" : isNonRecurringDeposit ? "black" : "white",
      beforeProductExisted,
    };
  }

  }
  export interface GroupedLiquiditySummary {
    liquidityDateCategory: string;
    totalAmount: number;
    objectList: LiquiditySummaryDetail[];
  }
  export interface GroupedLiquiditySummaryMap {
    [liquidityDateCategory: string]: {
      totalAmount: number;
      objectList: LiquiditySummaryDetail[];
    };
  }
  
  export interface LiquiditySummaryDetail {
    product_type: number;
    handler_code: string;
    policy_number: string;
    amount: number;
    liquidityDate: string | null;
    liquidityDateCategory: string;
  }

  
  